import { getErrorMessage } from "@/apis/apiErrorHandler";
import httpClient from "@/apis/httpClient";
import { formatCurrency } from "@/utils/number";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "sonner";
import { getNextSlotDeliveryTime, useCart } from "./useCart";
import { useStoreStore } from "@/components/stores/hooks/useStore";

export interface OrderMeta extends TrainMeta {
  deviceId: string;
  deviceName: string;
  deviceOs: string;
  deviceIp: string;
  deviceToken: string;
  [key: string]: unknown
}

export interface TrainMeta {
  trainCode?: string
  coachCode?: string
  seatNumber?: string
}

export interface PromotionProductSelectedDto {
  promotionId: string;
  productId: number;
  giftId: string;
}

export interface ElectronicInvoice {
  isElectronicInvoice?: boolean;
  companyName: string;
  taxCode: string;
  address: string;
  email: string;
}

export enum SaleType {
  DineIn = 'DineIn',
  PickUp = 'PickUp',
  Delivery = 'Delivery',
}

export enum ScanForm {
  /** QR scan tại cửa hàng, dùng để vào trang chính hoặc landing */
  STORE = 'STORE',

  /** QR scan tại bàn, dùng để gọi món, biết được số bàn */
  DINE_IN = 'DINE_IN',

  /** QR scan từ hóa đơn/bill để đánh giá */
  BILL = 'BILL',

  /** QR từ chiến dịch marketing, banner, mạng xã hội */
  CAMPAIGN = 'CAMPAIGN',

  /** QR từ Zalo OA, Messenger hoặc bên thứ ba */
  SOCIAL = 'SOCIAL',
}

export enum LocationType {
  NEAR_BY = 'NEAR_BY',
  SELECT_ADDRESS = 'SELECT_ADDRESS',
}

export interface CreateOrderDto {
  contactName?: string;
  contactPhone?: string;
  bankCode?: string;
  storeId: number | null;
  shippingMethod?: string;
  paymentMethod?: string;
  saleType?: SaleType.Delivery | SaleType.DineIn | SaleType.PickUp;
  redeemPoint?: number;
  rankDiscount?: number;
  rankDiscountAmount?: number;
  source?: string;
  areaCode?: string;
  menuProfileId?: string;
  orderNote?: string;
  returnUrl?: string;
  deliveryTime?: string;
  products?: any;
  cartAmount?: number;
  brandCode?: string;
  couponCodes?: any[];
  voucherCodes?: any[];
  discountCodes?: any[];
  shippingData?: any;
  electronicInvoice?: ElectronicInvoice;
  orderMeta?: OrderMeta;
  promotionProductSelectedList?: PromotionProductSelectedDto[]
  /**
   * KTN-472 [BE] Custom menu món hiển thị tương ứng với từng cửa hàng và phân quyền bật tắt món
   */
  validateDisableProductMenuStore?: any[];
  tableId?: string | null; // Thêm trường tableId để gửi lên API tạo đơn hàng
}

const getValidatedDeliveryTime = (time: string | undefined | null) => {
  const now = dayjs().add(5, "minute")
  const deliveryTime = dayjs(time)
  return deliveryTime.isBefore(now)
    ? getNextSlotDeliveryTime(now, 15, 10).format()
    : time
}

export default function useValidateOrder() {
  const { core, updateCore } = useCart()
  const { resetStore } = useStoreStore()

  /**
   * Kiểm tra và cập nhật thời gian giao hàng nếu cần thiết.
   * Nếu thời gian giao hàng đã qua thời gian hiện tại,
   */
  if (core?.deliveryTime) {
    const validatedTime = getValidatedDeliveryTime(core.deliveryTime)
    if (validatedTime !== core.deliveryTime) {
      updateCore({ deliveryTime: validatedTime })
    }
  }

  return useMutation({
    mutationFn: async (order: CreateOrderDto) => {
      order.deliveryTime = getValidatedDeliveryTime(core?.deliveryTime)

      try {
        // const res = await httpClient.post('/api/v2/Order/ValidateOrder', order)
        const res = await httpClient.post('/api/guest/v1/GuestOrder/ValidateOrderV2', order)

          if (!res.data?.success) {
            throw new Error('Không thể xác nhận đơn hàng. Vui lòng thử lại sau.')
          }

          const data = res.data?.result

          return {
            order_summary: [
              {
                key: 'sub_total',
                label: 'Thành tiền',
                value: formatCurrency(data?.subtotal)
              },
              data?.discountAmount && {
                key: 'discount_total',
                label: 'Khuyến mãi',
                value: `-${formatCurrency(data?.discountAmount)}`
              },
              data?.deliveryFee && {
                key: 'delivery_fee',
                label: 'Phí giao hàng',
                value: formatCurrency(data?.deliveryFee)
              },
              {
                key: 'total',
                label: 'Tổng thanh toán',
                value: formatCurrency(data?.orderTotal)
              }
            ],
            delivery_time: data?.deliveryTime ? dayjs(data?.deliveryTime).tz('Asia/Bangkok').format() : dayjs().add(30, 'minute').tz('Asia/Bangkok').format(),
            original_data: res.data?.result,
          }
      } catch (error: any) {
        const code = error?.response?.data?.error?.code
        // const message = error?.response?.data?.error?.message

        if (code === 250) {
          // updateCore({
          //   storeId: 0,
          // })
          // resetStore()
          const err = new Error('Địa chỉ giao hàng không hợp lệ.');
          (err as any).code = 250
          throw err
        }

        console.error('useValidateOrder error 1:', error)
        throw new Error(getErrorMessage(error))
      }
    },
    onError: (error: any) => {
      // Nếu là lỗi 250 thì không show toast
      if (error?.code === 250) return
      console.log('useValidateOrder error', error)
      toast.error(error?.message)
    }
  })
}
